/**
 * timeline/useTimelineState.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Custom hook — encapsulates all timeline UI state (view mode, anchor date,
 * collapsed sections) so TimelineBoard stays declarative.
 *
 * Returns nothing that requires the parent to know about date internals —
 * only derived values (columns, gridStart, gridEnd) are exposed.
 */

import { useState, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import { buildColumns } from './timelineUtils';
import { CELL_WIDTH } from './constants';

export default function useTimelineState() {
    const [viewMode, setViewMode] = useState('week');
    const [anchorDate, setAnchorDate] = useState(() => dayjs().startOf('day'));
    const [collapsed, setCollapsed] = useState({}); // { [sectionKey]: boolean }

    // ── Navigate prev / next / today ─────────────────────────────────────────
    // Step sizes — page by ~half the visible window so context is preserved.
    // Visible columns: day=21, week=12, month=9, quarter=6
    const step = { day: 10, week: 6, month: 4, quarter: 3 };

    const goPrev = useCallback(() => {
        setAnchorDate((d) => d.subtract(step[viewMode], viewMode === 'day' ? 'day' : viewMode === 'week' ? 'week' : 'month'));
    }, [viewMode]);

    const goNext = useCallback(() => {
        setAnchorDate((d) => d.add(step[viewMode], viewMode === 'day' ? 'day' : viewMode === 'week' ? 'week' : 'month'));
    }, [viewMode]);

    const goToday = useCallback(() => setAnchorDate(dayjs().startOf('day')), []);

    const changeViewMode = useCallback((mode) => {
        setViewMode(mode);
        setAnchorDate(dayjs().startOf('day')); // reset to today when switching modes
    }, []);

    // ── Derived columns ───────────────────────────────────────────────────────
    const columns = useMemo(() => buildColumns(viewMode, anchorDate), [viewMode, anchorDate]);

    const gridStart = useMemo(() => columns[0]?.start || dayjs(), [columns]);
    const gridEnd = useMemo(() => columns[columns.length - 1]?.end || dayjs(), [columns]);

    const cellWidth = CELL_WIDTH[viewMode] || 90;
    const gridWidthPx = columns.length * cellWidth;

    // ── Section collapse toggle ───────────────────────────────────────────────
    const toggleSection = useCallback((sectionKey) => {
        setCollapsed((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
    }, []);

    const isSectionCollapsed = useCallback(
        (sectionKey) => !!collapsed[sectionKey],
        [collapsed]
    );

    return {
        viewMode,
        anchorDate,
        columns,
        gridStart,
        gridEnd,
        cellWidth,
        gridWidthPx,
        goPrev,
        goNext,
        goToday,
        changeViewMode,
        toggleSection,
        isSectionCollapsed,
    };
}
